// User preferences for desktop notifications + chime sound.
//
// Persisted to localStorage (per-browser, per-user-on-this-device) so a
// teammate who turns on alerts on their work laptop doesn't suddenly
// get them on their phone too. Read at mount time + on visibility
// change so a toggle from the Settings page takes effect immediately
// in already-mounted tabs.
//
// Defaults are opt-OUT — the SPA never spawns a desktop notification
// without the user explicitly enabling it AND granting browser
// permission. This matches the assist-session attention hook's
// behaviour (use-assist-attention.ts) and is the right default for an
// app that runs in a tab the user may have opened-and-forgotten.

import { useEffect, useState } from 'react';

export interface NotificationPrefs {
  /** Master switch. When false, the hook does nothing — no spawn, no sound. */
  enabled: boolean;
  /** Play a short chime alongside the OS notification banner. */
  sound: boolean;
}

const STORAGE_KEY = 'pa-notification-prefs-v1';

export const DEFAULT_PREFS: NotificationPrefs = {
  enabled: false,
  sound: true,
};

export function readPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
    return {
      enabled: typeof parsed.enabled === 'boolean' ? parsed.enabled : DEFAULT_PREFS.enabled,
      sound: typeof parsed.sound === 'boolean' ? parsed.sound : DEFAULT_PREFS.sound,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function writePrefs(prefs: NotificationPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    // Fire a custom event so other mounted hooks in the same tab pick
    // up the change immediately — the native `storage` event only
    // fires across tabs, not within the same tab.
    window.dispatchEvent(new CustomEvent('pa-notification-prefs-change'));
  } catch {
    // Quota / private mode — best-effort.
  }
}

/** React hook that returns the live prefs + a setter that writes to
 *  localStorage AND keeps every mounted consumer of this hook in sync
 *  (so toggling in Settings flips the hook running at AuthedLayout
 *  level on the same tab, without needing a reload). */
export function useNotificationPrefs(): {
  prefs: NotificationPrefs;
  setPrefs: (next: Partial<NotificationPrefs>) => void;
} {
  const [prefs, setLocal] = useState<NotificationPrefs>(() => readPrefs());

  useEffect(() => {
    function refresh() {
      setLocal(readPrefs());
    }
    window.addEventListener('pa-notification-prefs-change', refresh);
    // Cross-tab sync — `storage` fires when another tab on the same
    // origin writes to localStorage.
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) refresh();
    });
    return () => {
      window.removeEventListener('pa-notification-prefs-change', refresh);
    };
  }, []);

  const setPrefs = (next: Partial<NotificationPrefs>) => {
    const merged = { ...readPrefs(), ...next };
    setLocal(merged);
    writePrefs(merged);
  };

  return { prefs, setPrefs };
}
